/**
 * Centralized Klarna Configuration
 * This module provides a single source of truth for Klarna API configuration
 * and utilities to be used across the application.
 */

// Initialize global Klarna cache
if (!window.klarnaGlobalCache) {
  window.klarnaGlobalCache = {};
}

// Initialize Klarna configuration on window object
if (!window.klarnaConfig) {
  window.klarnaConfig = {
    // Get the appropriate middleware URL based on environment
    getMiddlewareUrl: () => {
      return window.location.hostname === 'www.wndsr.dev'
        ? 'https://shopify-product-service.wndsr.dev/klarna-payments-osm'
        : 'https://shopify-product-service.wndsr.io/klarna-payments-osm';
    },

    // Build a full API URL with metafield keys
    buildApiUrl: (key) => {
      if (!key) return null;
      const baseUrl = window.klarnaConfig.getMiddlewareUrl();
      return `${baseUrl}?metafield_keys=${encodeURIComponent(key)}`;
    },

    // Fetch options for Klarna API calls
    fetchOptions: {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
      },
    },
  };
}

/**
 * Fetch Klarna data from API with caching
 * @param {string} key - The Klarna API key (e.g., 'price_usd_5000')
 * @returns {Promise<Object|null>} - Klarna data or null
 */
export const fetchKlarnaData = (key) => {
  if (!key) return Promise.resolve(null);

  // Check cache first
  if (window.klarnaGlobalCache[key]) {
    return Promise.resolve(window.klarnaGlobalCache[key]);
  }

  // Use centralized Klarna configuration
  const url = window.klarnaConfig.buildApiUrl(key);
  if (!url) return Promise.resolve(null);

  return fetch(url, window.klarnaConfig.fetchOptions)
    .then((response) => {
      if (!response.ok) throw new Error('Klarna fetch failed ' + response.status);
      return response.json();
    })
    .then((json) => {
      // API returns array: [{key: "...", value: {...}}]
      let data = null;
      if (Array.isArray(json) && json.length > 0) {
        data = json[0].value;
      }

      // Store in the global cache for subsequent variant changes.
      if (data) {
        window.klarnaGlobalCache[key] = data;
      }

      return data;
    })
    .catch((err) => {
      return null;
    });
};

export default window.klarnaConfig;
